<!-- The tab shell. THIS FILE IS YOURS.

     IonTabs wraps its OWN IonRouterOutlet, and that is what gives each tab a
     separate navigation stack: pushing /tabs/items/3 leaves the tab bar in
     place with a working back button, and switching away and back returns you
     to the detail screen rather than the top of the list. A single top-level
     outlet with a tab bar beside it cannot do that — it has one stack, so
     every tab switch is a replace and every push loses the bar.

     TWO PROPS ON EVERY TAB BUTTON, AND BOTH ARE LOAD-BEARING:
       `tab`   the id IonTabs tracks it by
       `href`  what IonTabs matches against the current route to decide which
               button is active

     The href is NOT followed as a link — IonTabButton calls
     navManager.changeTab(), which pushes through the router — so it works
     under the memory history this app uses. A button without an href is a tab
     that navigates fine and never highlights, which is a confusing five
     minutes to debug because nothing errors.

     THE IonPage WRAPPER IS REQUIRED. IonTabs is not itself a page, so a shell
     whose root is IonTabs gives the PARENT outlet nothing with `.ion-page` to
     transition, and Ionic warns "the view you are trying to render does not
     have the required <ion-page> component" on every tab change. It renders
     anyway, which is why this is easy to leave broken: what you lose is the
     enter/leave animation and the page lifecycle events. -->
<template>
  <IonPage>
    <IonTabs>
      <IonRouterOutlet />

      <IonTabBar ref="barRef" slot="bottom">
        <!-- A FLAT v-for, NOT a <template v-for>. IonTabs reads its slot
             children directly to build its tab bookkeeping and does not unwrap
             fragments, so wrapping these makes every child a Fragment vnode and
             the whole thing throws `Cannot read properties of undefined`. -->
        <IonTabButton
          v-for="t in TABS"
          :key="t.name"
          :tab="t.name"
          :href="t.path"
        >
          <!-- Label only. See the note in router.js: the app has no accent
               colour and no icon set, so the bar is type. -->
          <IonLabel>{{ tabLabel(t, shownModes.length) }}</IonLabel>
        </IonTabButton>

      </IonTabBar>
    </IonTabs>
  </IonPage>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {
  IonPage, IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonLabel,
} from '@ionic/vue';
import { useRoute } from 'vue-router';
import { TABS } from '../router.js';
import { shownModes } from '../app-state.js';
import { tabLabel } from '../tab-label.js';
import { originOf, tabOf } from '../nav-origin.js';

/**
 * WHICH TAB A PUSHED PAGE IS IN.
 *
 * `ion-tab-bar` picks its selected button by matching the current path against
 * that button's own `href` — `/tabs/journey/2` matches none of the four, so
 * every pushed screen in this app lit nothing at all and the bar read as four
 * dim words over a page you had plainly navigated to. A journey belongs to the
 * tab it was opened from: Trains when a planned row on the board opened it,
 * Lines when Your options did from a line.
 *
 * So the tab comes from the SAME RECORD the back chip reads
 * (`src/nav-origin.js`), which is what stops the chip and the highlight ever
 * disagreeing, and falls back to the tab the screen declares for itself when
 * nobody pushed it.
 *
 * WRITTEN AS `selectedTab`, which is Ionic's own mechanism — the property
 * `IonTabBar` sets itself when a path does match, and the one `ion-tab-bar`
 * reads to mark a button. Nothing here touches a class or a style: the bar goes
 * on deciding how a selected tab looks. It is re-asserted after every
 * navigation because Ionic writes the empty string over it whenever the path
 * matches no href, which is exactly the case this exists for.
 */
const route = useRoute();
const barRef = ref(null);

const selected = computed(() => originOf(route.fullPath)?.tab ?? tabOf(route.name) ?? '');

function apply() {
  const el = barRef.value?.$el;
  if (el) el.selectedTab = selected.value;
}

// After the render, and after Ionic's own history listener has had its say.
onMounted(() => nextTick(apply));
watch([selected, () => route.fullPath], () => nextTick(apply), { flush: 'post' });
</script>
